import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  FundraisingFund, 
  FundraisingTermSheet, 
  TERM_SHEET_STATUSES, 
  FUND_STAGES,
  FUND_PRIORITIES,
  FundStage, 
  FundPriority,
  TermSheetStatus,
  calculateTermSheetMetrics 
} from '../../types';
import { Card, Button, Select, Input, Table, useTable } from "@repo/o37-ui";
import { Search, ExternalLink, MoreHorizontal, FileText } from "lucide-react";

// Flattened term sheet with fund data
interface TermSheetWithFund extends FundraisingTermSheet {
  fund: FundraisingFund;
}

interface TermSheetLeadTableProps {
  funds: FundraisingFund[];
  onEditTermSheet?: (termSheet: FundraisingTermSheet, fund: FundraisingFund) => void;
  onStatusChange?: (termSheetId: string, fundId: string, newStatus: TermSheetStatus) => void;
}

type GroupingMode = 'none' | 'stage' | 'tier' | 'status';

export default function TermSheetLeadTable({ funds, onEditTermSheet, onStatusChange }: TermSheetLeadTableProps) {
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<FundStage | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<FundPriority | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<TermSheetStatus | 'all'>('all');

  // Flatten term sheets with fund data
  const allTermSheets: TermSheetWithFund[] = useMemo(() => {
    const sheets: TermSheetWithFund[] = [];
    funds.forEach(fund => {
      (fund.termSheets || []).forEach(ts => {
        sheets.push({ ...ts, fund });
      });
    });
    return sheets;
  }, [funds]);

  // Determine grouping mode based on filters
  const groupingMode: GroupingMode = useMemo(() => {
    if (stageFilter !== 'all' && priorityFilter !== 'all') return 'none';
    if (stageFilter !== 'all' && priorityFilter === 'all') return 'tier';
    return 'stage'; // all stages + all tiers OR all stages + one tier
  }, [stageFilter, priorityFilter]);

  const filteredTermSheets = allTermSheets.filter(ts => {
    // Search filter
    const searchLower = search.toLowerCase();
    const matchesSearch = !search || 
      ts.fund.name.toLowerCase().includes(searchLower);

    // Stage filter (based on fund's stage)
    const matchesStage = stageFilter === 'all' || ts.fund.stage === stageFilter;

    // Priority filter (based on fund's priority)
    const matchesPriority = priorityFilter === 'all' || ts.fund.priority === priorityFilter;

    // Status filter
    const matchesStatus = statusFilter === 'all' || ts.status === statusFilter;

    return matchesSearch && matchesStage && matchesPriority && matchesStatus;
  });

  // Group term sheets based on grouping mode
  const groupedTermSheets = useMemo(() => {
    if (groupingMode === 'none') {
      return [{ key: 'all', label: '', funds: filteredTermSheets }];
    }
    
    if (groupingMode === 'stage') {
      const groups: { key: string; label: string; color: string; bgColor: string; funds: TermSheetWithFund[] }[] = [];
      FUND_STAGES.forEach(({ stage, label, color, bgColor }) => {
        const stageSheets = filteredTermSheets.filter(ts => ts.fund.stage === stage);
        if (stageSheets.length > 0) {
          groups.push({ key: stage, label, color, bgColor, funds: stageSheets });
        }
      });
      return groups;
    }
    
    // groupingMode === 'tier'
    const groups: { key: string; label: string; color: string; bgColor: string; funds: TermSheetWithFund[] }[] = [];
    const tierBgColors: Record<FundPriority, string> = {
      'tier_1': 'bg-amber-50',
      'tier_2': 'bg-slate-100',
      'tier_3': 'bg-slate-50',
    };
    FUND_PRIORITIES.forEach(({ priority, label }) => {
      const tierSheets = filteredTermSheets.filter(ts => ts.fund.priority === priority);
      if (tierSheets.length > 0) {
        groups.push({ 
          key: priority, 
          label, 
          color: 'text-neutral-700', 
          bgColor: tierBgColors[priority], 
          funds: tierSheets 
        });
      }
    });
    return groups;
  }, [filteredTermSheets, groupingMode]);

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
  };

  const getStatusConfig = (status: TermSheetStatus) => 
    TERM_SHEET_STATUSES.find(s => s.status === status) || { label: status, color: 'text-neutral-600', bgColor: 'bg-neutral-50' };

  const getPriorityConfig = (priority: FundPriority) =>
    FUND_PRIORITIES.find(p => p.priority === priority) || { label: priority, color: 'bg-neutral-400' };

  const columns = useMemo(() => [
    {
      id: "fund",
      header: "Fund",
      accessorFn: (row: any) => row.fund.name,
      cell: ({ row }: any) => {
        const ts = row.original as TermSheetWithFund;
        return (
          <Link 
            to={`/fundraising/funds/${ts.fund.id}`}
            className="flex items-center gap-3 group"
          >
            {ts.fund.logoUrl ? (
              <img 
                src={ts.fund.logoUrl} 
                alt={ts.fund.name}
                className="w-8 h-8 rounded object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded bg-neutral-100 flex items-center justify-center text-xs font-medium text-neutral-500">
                {ts.fund.name.charAt(0)}
              </div>
            )}
            <div>
              <div className="font-medium text-neutral-900 group-hover:underline decoration-neutral-400 underline-offset-2 decoration-dotted">
                {ts.fund.name}
              </div>
              {ts.fund.website && (
                <a 
                  href={ts.fund.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-neutral-400 hover:text-blue-600 flex items-center gap-1"
                >
                  Website <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </Link>
        );
      }
    },
    {
      id: "investmentAmount",
      header: "Investment",
      cell: ({ row }: any) => {
        const ts = row.original as TermSheetWithFund;
        return (
          <span className="font-semibold text-neutral-900">
            {formatCurrency(ts.investmentAmount)}
          </span>
        );
      }
    },
    {
      id: "valuation",
      header: "Valuation",
      cell: ({ row }: any) => {
        const ts = row.original as TermSheetWithFund;
        if (!ts.preMoneyValuation) {
          return <span className="text-neutral-300">—</span>;
        }
        const metrics = calculateTermSheetMetrics(ts.investmentAmount, ts.preMoneyValuation);
        return (
          <div>
            <div className="text-neutral-900">{formatCurrency(ts.preMoneyValuation)}</div>
            <div className="text-xs text-neutral-500">{metrics.dilutionPercentage.toFixed(1)}% dilution</div>
          </div>
        );
      }
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }: any) => {
        const ts = row.original as TermSheetWithFund;
        const statusConfig = getStatusConfig(ts.status);
        return (
          <div onClick={(e) => e.stopPropagation()}>
            {onStatusChange ? (
              <Select
                value={ts.status}
                onChange={(e) => onStatusChange(ts.id, ts.fund.id, e.target.value as TermSheetStatus)}
                className={`text-xs h-7 ${statusConfig.bgColor} border-0`}
              >
                {TERM_SHEET_STATUSES.map(({ status, label }) => (
                  <option key={status} value={status}>{label}</option>
                ))}
              </Select>
            ) : (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
            )}
          </div>
        );
      }
    },
    {
      id: "tier",
      header: () => <div className="text-center">Tier</div>,
      cell: ({ row }: any) => {
        const ts = row.original as TermSheetWithFund;
        const priorityConfig = getPriorityConfig(ts.fund.priority);
        return (
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium">
              <span className={`w-2 h-2 rounded-full ${priorityConfig.color}`} />
              {priorityConfig.label}
            </span>
          </div>
        );
      }
    },
    {
      id: "received",
      header: "Received",
      cell: ({ row }: any) => {
        const ts = row.original as TermSheetWithFund;
        return (
          <span className="text-neutral-500 text-xs">
            {formatTimeAgo(ts.receivedAt)}
          </span>
        );
      }
    },
    {
      id: "expires",
      header: "Expires",
      cell: ({ row }: any) => {
        const ts = row.original as TermSheetWithFund;
        if (!ts.expiresAt) {
          return <span className="text-neutral-300">—</span>;
        }
        const expiresDate = new Date(ts.expiresAt);
        const isExpired = expiresDate < new Date();
        const daysLeft = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        return (
          <span className={`text-xs ${isExpired ? 'text-red-500' : daysLeft <= 7 ? 'text-amber-600' : 'text-neutral-500'}`}>
            {isExpired ? 'Expired' : `${daysLeft}d left`}
          </span>
        );
      }
    },
    {
      id: "actions",
      header: "",
      size: 50,
      cell: ({ row }: any) => {
        const ts = row.original as TermSheetWithFund;
        return (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {ts.documentUrl && (
              <a
                href={ts.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 text-neutral-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="View Document"
              >
                <FileText className="w-4 h-4" />
              </a>
            )}
            {onEditTermSheet && (
              <Button
                variant="ghost"
                onClick={() => onEditTermSheet(ts, ts.fund)}
                className="h-7 w-7 p-0"
                icon={<MoreHorizontal className="w-4 h-4" />}
              />
            )}
          </div>
        );
      }
    }
  ], [onStatusChange, onEditTermSheet]);

  if (allTermSheets.length === 0) {
    return (
      <Card>
        <div className="p-8 text-center text-neutral-500">
          <FileText className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
          <p>No term sheets received yet</p>
          <p className="text-xs text-neutral-400 mt-1">Term sheets will appear here when funds send offers</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {/* Filters */}
      <div className="p-4 border-b border-neutral-100">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search by fund name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as FundStage | 'all')}
            className="w-28"
          >
            <option value="all">All Stages</option>
            {FUND_STAGES.map(({ stage, label }) => (
              <option key={stage} value={stage}>{label}</option>
            ))}
          </Select>

          <Select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as FundPriority | 'all')}
            className="w-24"
          >
            <option value="all">All Tiers</option>
            {FUND_PRIORITIES.map(({ priority, label }) => (
              <option key={priority} value={priority}>{label}</option>
            ))}
          </Select>

          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as TermSheetStatus | 'all')}
            className="w-28"
          >
            <option value="all">All Statuses</option>
            {TERM_SHEET_STATUSES.map(({ status, label }) => (
              <option key={status} value={status}>{label}</option>
            ))}
          </Select>

          <div className="text-sm text-neutral-500 whitespace-nowrap ml-auto pl-4">
            {filteredTermSheets.length} of {allTermSheets.length} term sheets
          </div>
        </div>
      </div>

      {/* Grouped Tables */}
      <div className="divide-y divide-neutral-100">
        {groupedTermSheets.map((group) => (
          <GroupedTermSheetTable
            key={group.key}
            group={group}
            columns={columns}
            showHeader={groupingMode !== 'none'}
          />
        ))}
      </div>

      {filteredTermSheets.length === 0 && (
        <div className="p-8 text-center text-neutral-500">
          No term sheets match your filters
        </div>
      )}
    </Card>
  );
}

// Separate component for grouped table section
function GroupedTermSheetTable({ 
  group, 
  columns, 
  showHeader 
}: { 
  group: { key: string; label: string; color?: string; bgColor?: string; funds: TermSheetWithFund[] };
  columns: any[];
  showHeader: boolean;
}) {
  const [localPagination, setLocalPagination] = useState({ pageIndex: 0, pageSize: 50 });
  
  const { table, ...tableProps } = useTable({
    data: group.funds,
    columns,
    pagination: localPagination,
    onPaginationChange: setLocalPagination,
    rowCount: group.funds.length,
    resourceName: (plural) => `term sheet${plural ? "s" : ""}`,
  });

  if (group.funds.length === 0) return null;

  return (
    <div>
      {showHeader && group.label && (
        <div className={`px-4 py-2.5 ${group.bgColor || 'bg-neutral-50'} border-b border-neutral-100`}>
          <div className="flex items-center justify-between">
            <span className={`text-xs font-semibold uppercase tracking-wider ${group.color || 'text-neutral-600'}`}>
              {group.label}
            </span>
            <span className="text-xs text-neutral-400">
              {group.funds.length} term sheet{group.funds.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
      <Table {...tableProps} table={table} />
    </div>
  );
}
